import * as actions from '@/store/action-types.js'
import * as mutations from '@/store/mutation-types.js'
import * as getters from '@/store/getters-types.js'
import service from '@/services/member-type-correction-service.js'
import { adaptApplyResult, adaptPreview, adaptRun, adaptSummary } from '@/models/member-type-correction.js'

export const initialState = () => ({
  run: null, preview: null, summary: null, applyResult: null, loading: false, error: null,
  billingLoading: false, billingError: null, billingCreated: false
})

const candidates = (state) => state.preview?.candidates || []
const selectable = (candidate) => candidate.currentMemberType !== 'VEREIN'
const errorDetails = (error) => ({
  status: error?.response?.status || null,
  code: error?.response?.data?.code || null,
  message: error?.response?.data?.message || error?.message || 'Unbekannter Fehler',
  details: error?.response?.data?.details || null
})

async function request(commit, operation) {
  commit(mutations.SET_MEMBER_TYPE_CORRECTION_LOADING, true)
  commit(mutations.SET_MEMBER_TYPE_CORRECTION_ERROR, null)
  try {
    return await operation()
  } catch (error) {
    commit(mutations.SET_MEMBER_TYPE_CORRECTION_ERROR, errorDetails(error))
    throw error
  } finally {
    commit(mutations.SET_MEMBER_TYPE_CORRECTION_LOADING, false)
  }
}

export default {
  namespaced: true,
  state: initialState(),
  getters: {
    [getters.SELECTED_MEMBER_TYPE_CORRECTION_MEMBER_IDS]: (state) => candidates(state).filter((c) => c.selected && selectable(c)).map((c) => c.memberId),
    [getters.EXCLUDED_MEMBER_TYPE_CORRECTION_MEMBER_IDS]: (state) => candidates(state).filter((c) => !c.selected && selectable(c)).map((c) => c.memberId),
    [getters.HAS_MEMBER_TYPE_CORRECTION_CANDIDATES]: (state) => candidates(state).length > 0,
    [getters.HAS_MEMBER_TYPE_CORRECTION_WARNINGS]: (state) => (state.preview?.warnings?.length || 0) > 0 || candidates(state).some((c) => c.warning),
    [getters.CURRENT_MEMBER_TYPE_CORRECTION_STEP]: (state) => state.run?.currentStep || state.preview?.stepKey || null,
    [getters.IS_MEMBER_TYPE_CORRECTION_COMPLETED]: (state) => state.run?.status === 'COMPLETED' || state.summary?.status === 'COMPLETED'
  },
  mutations: {
    [mutations.SET_MEMBER_TYPE_CORRECTION_RUN](state, run) { state.run = run },
    [mutations.SET_MEMBER_TYPE_CORRECTION_PREVIEW](state, preview) { state.preview = preview },
    [mutations.SET_MEMBER_TYPE_CORRECTION_CANDIDATE_SELECTION](state, payload) {
      candidates(state).filter((c) => selectable(c) && (payload.memberId == null || c.memberId === payload.memberId)).forEach((c) => { c.selected = payload.selected })
    },
    [mutations.SET_MEMBER_TYPE_CORRECTION_LOADING](state, value) { state.loading = value },
    [mutations.SET_MEMBER_TYPE_CORRECTION_ERROR](state, value) { state.error = value },
    [mutations.SET_MEMBER_TYPE_CORRECTION_APPLY_RESULT](state, value) { state.applyResult = value },
    [mutations.SET_MEMBER_TYPE_CORRECTION_SUMMARY](state, value) { state.summary = value },
    [mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_LOADING](state, value) { state.billingLoading = value },
    [mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_ERROR](state, value) { state.billingError = value },
    [mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_CREATED](state, value) { state.billingCreated = value },
    [mutations.RESET_MEMBER_TYPE_CORRECTION_STATE](state) { Object.assign(state, initialState()) }
  },
  actions: {
    [actions.START_MEMBER_TYPE_CORRECTION_RUN]({ commit }, payload) {
      return request(commit, async () => {
        const run = adaptRun(await service.startRun(payload)); commit(mutations.SET_MEMBER_TYPE_CORRECTION_RUN, run); return run
      })
    },
    [actions.GET_MEMBER_TYPE_CORRECTION_RUN]({ commit }, runId) {
      return request(commit, async () => {
        const run = adaptRun(await service.getRun(runId)); commit(mutations.SET_MEMBER_TYPE_CORRECTION_RUN, run); return run
      })
    },
    [actions.GET_MEMBER_TYPE_CORRECTION_PREVIEW]({ commit }, { runId, stepKey }) {
      return request(commit, async () => {
        const preview = adaptPreview(await service.getPreview(runId, stepKey)); commit(mutations.SET_MEMBER_TYPE_CORRECTION_PREVIEW, preview); return preview
      })
    },
    [actions.APPLY_MEMBER_TYPE_CORRECTION_STEP]({ commit, state, getters: localGetters }, { comment = null } = {}) {
      return request(commit, async () => {
        const result = adaptApplyResult(await service.applyStep(state.run.runId, state.run.currentStep, {
          selectedMemberIds: localGetters[getters.SELECTED_MEMBER_TYPE_CORRECTION_MEMBER_IDS],
          excludedMemberIds: localGetters[getters.EXCLUDED_MEMBER_TYPE_CORRECTION_MEMBER_IDS], comment: comment || null
        }))
        commit(mutations.SET_MEMBER_TYPE_CORRECTION_APPLY_RESULT, result); return result
      })
    },
    [actions.SKIP_MEMBER_TYPE_CORRECTION_STEP]({ commit, state }, { comment = null } = {}) {
      return request(commit, async () => {
        const result = adaptApplyResult(await service.skipStep(state.run.runId, state.run.currentStep, { comment: comment || null }))
        commit(mutations.SET_MEMBER_TYPE_CORRECTION_APPLY_RESULT, result); return result
      })
    },
    [actions.GET_MEMBER_TYPE_CORRECTION_SUMMARY]({ commit }, runId) {
      return request(commit, async () => {
        const summary = adaptSummary(await service.getSummary(runId)); commit(mutations.SET_MEMBER_TYPE_CORRECTION_SUMMARY, summary); return summary
      })
    },
    async [actions.CREATE_MEMBER_TYPE_CORRECTION_BILLING]({ commit }) {
      commit(mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_LOADING, true)
      commit(mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_ERROR, null)
      try {
        const result = await service.createBilling()
        commit(mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_CREATED, true)
        return result
      } catch (error) {
        commit(mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_ERROR, errorDetails(error))
        throw error
      } finally {
        commit(mutations.SET_MEMBER_TYPE_CORRECTION_BILLING_LOADING, false)
      }
    },
    [actions.RESET_MEMBER_TYPE_CORRECTION]({ commit }) { commit(mutations.RESET_MEMBER_TYPE_CORRECTION_STATE) }
  }
}
